import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaReact, 
  FaJs, 
  FaHtml5, 
  FaCss3Alt, 
  FaNodeJs, 
  FaGitAlt,
  FaLaravel,
  FaAndroid,
  FaDatabase,
  FaCode,
  FaMobile,
  FaServer,
  FaVuejs,
  FaBootstrap,
  FaAngular,
    FaPython,
    FaLinux
} from 'react-icons/fa';
import { SiTypescript, SiTailwindcss, SiMysql, SiPhp, SiArduino, SiPostgresql, SiGraphql, SiN8N, SiDocker } from 'react-icons/si';

const Skills: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const skillCategories = [
    {
      title: 'Frontend Development',
      skills: [
        { name: 'React', icon: FaReact as any, level: 95, color: 'text-blue-500' },
        { name: 'Vue', icon: FaVuejs as any, level: 80, color: 'text-green-500' },
        { name: 'Angular', icon: FaAngular as any, level: 80, color: 'text-red-500' },
        { name: 'TypeScript', icon: SiTypescript as any, level: 90, color: 'text-blue-600' },
        { name: 'JavaScript', icon: FaJs as any, level: 95, color: 'text-yellow-500' },
        { name: 'HTML5', icon: FaHtml5, level: 98, color: 'text-orange-500' },
        { name: 'CSS3', icon: FaCss3Alt, level: 95, color: 'text-blue-400' },
        { name: 'Tailwind CSS', icon: SiTailwindcss as any, level: 90, color: 'text-cyan-500' },
        { name: 'Bootstrap', icon: FaBootstrap as any, level: 90, color: 'text-purple-500' },
      ]
    },
    {
      title: 'Backend Development',
      skills: [
        { name: 'Laravel', icon: FaLaravel as any, level: 85, color: 'text-red-500' },
        { name: 'Python', icon: FaPython as any, level: 80, color: 'text-blue-500' },
        { name: 'PHP', icon: SiPhp as any, level: 80, color: 'text-purple-500' },
        { name: 'Node.js', icon: FaNodeJs as any, level: 75, color: 'text-green-500' },
        { name: 'MySQL', icon: SiMysql as any, level: 85, color: 'text-blue-600' },
        { name: 'PostgreSQL', icon: SiPostgresql as any, level: 90, color: 'text-green-500' },
        { name: 'GraphQL', icon: SiGraphql as any, level: 80, color: 'text-purple-500' },
        { name: 'Database Design', icon: FaDatabase as any, level: 80, color: 'text-indigo-500' },
      ]
    },
    {
      title: 'Tools & Others',
      skills: [
        { name: 'Git', icon: FaGitAlt as any, level: 90, color: 'text-orange-600' },
        { name: 'N8N', icon: SiN8N as any, level: 80, color: 'text-green-500' },
        { name: 'Docker', icon: SiDocker as any, level: 80, color: 'text-blue-500' },
        { name: 'Linux', icon: FaLinux as any, level: 80, color: 'text-black-500' },
        { name: 'Code Review', icon: FaCode as any, level: 88, color: 'text-purple-600' },
        { name: 'API Integration', icon: FaServer as any, level: 85, color: 'text-gray-600' },
      ]
    }
  ];

  return (
    <section id="skills" className="section-padding bg-gray-50">
      <div className="container-custom">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="gradient-text">Skills & Technologies</span>
          </motion.h2>
          <motion.div
            variants={itemVariants}
            className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto mb-8"
          />
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            A comprehensive set of skills and technologies I use to bring ideas to life
          </motion.p>
        </motion.div>

        <div className="space-y-16">
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">
                {category.title}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.skills.map((skill, skillIndex) => (
                  <motion.div
                    key={skill.name}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: skillIndex * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    className="group"
                  >
                    <div className="bg-gray-50 rounded-xl p-6 hover:bg-gradient-to-br hover:from-blue-50 hover:to-cyan-50 transition-all duration-300 h-full">
                      <div className="flex items-center space-x-4 mb-4">
                        <motion.div
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.6 }}
                          className={`text-3xl ${skill.color}`}
                        >
                          <skill.icon />
                        </motion.div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">
                            {skill.name}
                          </h4>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Proficiency</span>
                          <span>{skill.level}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${skill.level}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: skillIndex * 0.1 }}
                            className={`h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full`}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Skills */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Additional Expertise</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                'OAuth 2.0', 'RESTful APIs', 'Responsive Design', 'Cross-browser Compatibility',
                'Performance Optimization', 'SEO', 'Agile Methodologies', 'Project Management',
                'Code Review', 'Testing', 'Debugging', 'Version Control'
              ].map((skill, index) => (
                <motion.span
                  key={skill}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium hover:bg-white/30 transition-colors duration-300"
                >
                  {skill}
                </motion.span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
